/**
 * @file components/auth/AuthProvider.tsx
 * @description 全局認證初始化 Provider。
 *
 * 職責：
 * - 應用啟動時 GET /api/auth/me 校驗 session
 * - 已 persist 的 user 走樂觀路徑：立即顯示 UI，背景校驗
 * - 未 persist 時顯示全屏 Spin，等 /me 完成
 * - 校驗失敗（401）清 user，由路由守衛跳 login
 *
 * 與 _app/route.tsx 的關係：
 *  - _app/route.tsx 的 beforeLoad 處理「直接打開 /_app/* 路由時」的兜底還原
 *  - 本 Provider 處理「應用啟動時」的常規校驗（也順便讓 / 等公開頁面知道誰登入了）
 *
 * 使用位置：在 __root.tsx 的 component 中，包裹 <Outlet />。
 */

import { useEffect, type ReactNode } from 'react';
import { Spin } from 'antd';
import { useAuthStore, type AuthUser } from '@/lib/auth-context';
import { ApiError, apiClient } from '@/lib/api-client';

/**
 * 認證 Provider 元件。
 *
 * 啟動策略：
 *  - 若 persist 還原出 user：loading 立即設 false（不 spin），背景校驗 /me；
 *    校驗失敗（401）才清掉 user，由路由守衛接手跳 login。
 *  - 若 persist 沒有 user：保持 loading=true 顯示 Spin，等 /me 完成再渲染。
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { loading, user, setUser, setLoading } = useAuthStore();

  useEffect(() => {
    // 樂觀路徑：persist 還原出 user 就立即放行，UI 不轉圈
    const hasPersistedUser = useAuthStore.getState().user != null;
    if (hasPersistedUser) setLoading(false);

    // 不論有沒有 persist user，都背景校驗一次 /me，確保 cookie 仍有效
    apiClient
      .get<{ user: AuthUser }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          setUser(null);
          return;
        }
        console.warn('[auth] /api/auth/me unavailable; keeping persisted user', err);
      })
      .finally(() => {
        if (!hasPersistedUser) setLoading(false);
      });
    // setUser / setLoading 是穩定的 zustand action 引用，安全列入 deps
  }, [setUser, setLoading]);

  // 沒有 persist user 時才轉圈，避免已登入用戶刷新時看到不必要的 Spin
  if (loading && user == null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
}
